import React, { useState } from "react";
import { Box, Select, MenuItem, FormControl, FormControlLabel, IconButton, Switch, Tooltip } from "@mui/material";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import { alpha } from "@mui/material/styles";
import { orange } from "@/theme/colors";
import SearchBar from "@/components/Importable/SearchBar";
import PrioritizeSavedSwitch from "@/components/Importable/PrioritizeSavedSwitch";
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
const FilterBar = ({
  accentColor = orange.primary,
  searchSubjectLabel = "Organizations",
  filterMin = 0,
  itemCount,
  selectTarget = "Filters",
  search = false,
  searchPlaceholder = "Search...",
  searchValue: controlledSearchValue,
  onSearchChange,
  searchScope = "all",
  searchMinChars,
  onSearch,
  filter1,
  filter2,
  filter3,
  showPrioritizeSaved = false,
  extraToggle,
  prioritizeSaved = false,
  onPrioritizeSavedChange,
  viewMode = "grid",
  onViewModeChange
}) => {
  const [internalSearchValue, setInternalSearchValue] = useState("");
  const searchValue = controlledSearchValue !== undefined ? controlledSearchValue : internalSearchValue;
  const handleSearchChange = value => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearchValue(value);
    }
  };

  // Below the threshold the filter selects are suppressed; search and
  // the view toggle stay. itemCount omitted => always show.
  const showFilters = filterMin <= 0 || itemCount === undefined || itemCount >= filterMin;
  const renderFilter = filter => {
    // A select offering only "All X" with no real options — and no
    // value already chosen — is noise. Regulations' Type/Category are
    // empty today; Region has data, so it stays.
    if (filter.options.length === 0 && !filter.value) return null;
    return /*#__PURE__*/_jsx(FormControl, {
      size: "small",
      sx: {
        minWidth: 160
      },
      children: /*#__PURE__*/_jsxs(Select, {
        displayEmpty: true,
        value: filter.value,
        onChange: e => filter.onChange(e.target.value),
        children: [!filter.hideAll && /*#__PURE__*/_jsxs(MenuItem, {
          value: "",
          children: ["All ", filter.pluralName ?? `${filter.name}s`]
        }), filter.options.map(option => /*#__PURE__*/_jsx(MenuItem, {
          value: option,
          children: option
        }, option))]
      })
    });
  };
  const [searchFocused, setSearchFocused] = useState(false);
  return /*#__PURE__*/_jsxs(Box, {
    "data-select-target": selectTarget,
    sx: {
      mb: 3,
      p: 2,
      backgroundColor: "#fff",
      borderRadius: 2,
      boxShadow: 1,
      display: "flex",
      gap: 2,
      alignItems: "center",
      flexWrap: "wrap"
    },
    children: [search && /*#__PURE__*/_jsx(Tooltip, {
      title: `${searchSubjectLabel} will be filtered as you type`,
      open: searchFocused || searchValue.length > 0,
      placement: "top",
      arrow: true,
      children: /*#__PURE__*/_jsx(Box, {
        sx: {
          minWidth: 220,
          flex: "1 1 220px"
        },
        children: /*#__PURE__*/_jsx(SearchBar, {
          scope: searchScope,
          mode: "instant",
          minChars: searchMinChars,
          placeholder: searchPlaceholder,
          value: searchValue,
          onValueChange: handleSearchChange,
          onSearch: onSearch,
          onFocus: () => setSearchFocused(true),
          onBlur: () => setSearchFocused(false),
          sx: {
            width: "100%",
            minWidth: 0
          }
        })
      })
    }), onViewModeChange && /*#__PURE__*/_jsx(Tooltip, {
      title: viewMode === "grid" ? "Switch to list view" : "Switch to grid view",
      placement: "top",
      arrow: true,
      children: /*#__PURE__*/_jsx(IconButton, {
        "aria-label": viewMode === "grid" ? "Switch to list view" : "Switch to grid view",
        onClick: () => onViewModeChange(viewMode === "grid" ? "list" : "grid"),
        sx: {
          border: `1px solid ${alpha(accentColor, 0.5)}`,
          borderRadius: 1,
          color: accentColor,
          "&:hover": {
            backgroundColor: alpha(accentColor, 0.08)
          }
        },
        children: viewMode === "grid" ? /*#__PURE__*/_jsx(ViewListIcon, {}) : /*#__PURE__*/_jsx(ViewModuleIcon, {})
      })
    }), showFilters && filter1 && renderFilter(filter1), showFilters && filter2 && renderFilter(filter2), showFilters && filter3 && renderFilter(filter3), (showPrioritizeSaved || extraToggle) && /*#__PURE__*/_jsxs(Box, {
      sx: {
        ml: "auto",
        display: "flex",
        flexDirection: "column",
        // Both switches share one left edge so the column
        // reads as a pair, not two offset controls.
        alignItems: "flex-start",
        rowGap: 0.25
      },
      children: [/*#__PURE__*/_jsx(PrioritizeSavedSwitch, {
        show: showPrioritizeSaved,
        checked: prioritizeSaved,
        onChange: checked => onPrioritizeSavedChange?.(checked),
        accentColor: accentColor
      }), extraToggle && /*#__PURE__*/_jsx(FormControlLabel, {
        control: /*#__PURE__*/_jsx(Switch, {
          size: "small",
          checked: extraToggle.checked,
          onChange: e => extraToggle.onChange(e.target.checked),
          sx: {
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: accentColor
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: accentColor
            }
          }
        }),
        label: extraToggle.label,
        sx: {
          m: 0,
          "& .MuiFormControlLabel-label": {
            fontSize: "0.875rem"
          }
        }
      })]
    })]
  });
};
export default FilterBar;